import { type FC } from 'react';

import {
  DialogContextProvider,
  UpsellBannerWithIcon,
} from '@xing-com/crate-entity-pages-common';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useFeatureSwitch } from '@xing-com/hub';
import { Column, Row } from '@xing-com/xing-grid';

import { HeaderContextProvider } from '../../context/header-context/header-context';
import { CoverMediaContainer } from '../cover-media/cover-media-container';
import { ENTITY_PAGES_UNFENCE_HEADER } from '../edit/edit-cover/edit-cover-menu';
import { LogoArea } from '../logo-area/logo-area';
import { PrimaryArea } from '../primary-area/primary-area';
import { SecondaryArea } from '../secondary-area/secondary-area';
import { TertiaryArea } from '../tertiary-area/tertiary-area';

import { HeaderSkeleton } from './header-skeleton';
import * as Styled from './header.styles';

type HeaderProps = {
  moduleType?: string;
};

export const Header: FC<HeaderProps> & {
  Skeleton: typeof HeaderSkeleton;
} = ({ moduleType }) => {
  const { isLoggedOut } = useLoginState();
  const { pageContext, isSubpage } = usePageContext();

  const { upsellActive, socialProof } = pageContext;
  const hasFenceFS = useFeatureSwitch(ENTITY_PAGES_UNFENCE_HEADER, false);

  return (
    <>
      {!hasFenceFS && upsellActive && (
        <Styled.UpsellLayout>
          <UpsellBannerWithIcon
            headerCopyKey="EP_UPSELL_HEADER_TOP_HEADER"
            bodyCopyKey="EP_UPSELL_BODY_TOP_HEADER"
            moduleName="headermodule"
          />
        </Styled.UpsellLayout>
      )}
      <DialogContextProvider>
        <HeaderContextProvider moduleType={moduleType}>
          {({ hasTertiaryArea }) => (
            <Styled.Container data-testid="HEADER-CONTAINER">
              <CoverMediaContainer />
              <Styled.GridContainer>
                <Row>
                  <Column size={12} className="col">
                    <Styled.TopArea data-testid="HEADER-TOP-AREA">
                      <LogoArea />
                      <Styled.SideArea
                        $isLoggedOut={isLoggedOut}
                        $isSubpage={!!isSubpage}
                        $noSocialProof={
                          !(socialProof && socialProof?.total > 0)
                        }
                      >
                        <PrimaryArea />
                        <SecondaryArea />
                      </Styled.SideArea>
                    </Styled.TopArea>
                    {hasTertiaryArea ? <TertiaryArea /> : null}
                  </Column>
                </Row>
              </Styled.GridContainer>
            </Styled.Container>
          )}
        </HeaderContextProvider>
      </DialogContextProvider>
    </>
  );
};

Header.Skeleton = HeaderSkeleton;
